import {
  computePointsAreaHa,
  computePointsLengthMeters,
  computePointsPerimeterM,
} from "./mapMeasurements";

export const TRACK_SNAP_MAX_M = 28;
export const CROSSING_CLUSTER_M = 3.5;

const EARTH_RADIUS_M = 6371000;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function toDeg(rad) {
  return (rad * 180) / Math.PI;
}

export function haversineMeters(pointA, pointB) {
  if (!pointA || !pointB) return 0;
  const lat1 = toRad(pointA.lat);
  const lat2 = toRad(pointB.lat);
  const dLat = lat2 - lat1;
  const dLng = toRad(pointB.lng - pointA.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function bearingDegrees(from, to) {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export function offsetPoint(origin, bearingDeg, distanceM) {
  const angular = distanceM / EARTH_RADIUS_M;
  const br = toRad(bearingDeg);
  const lat1 = toRad(origin.lat);
  const lng1 = toRad(origin.lng);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) +
      Math.cos(lat1) * Math.sin(angular) * Math.cos(br)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2)
    );
  return { lat: toDeg(lat2), lng: toDeg(lng2) };
}

export function normalizeBearing(deg) {
  return ((Number(deg) % 360) + 360) % 360;
}

export function perpendicularBearing(headingDeg) {
  return normalizeBearing(Number(headingDeg) + 90);
}

function localMetersPerDegree(origin) {
  const mPerDegLat = 110574;
  const mPerDegLng = 111320 * Math.cos(toRad(origin.lat));
  return { mPerDegLat, mPerDegLng };
}

function toXY(point, origin) {
  const { mPerDegLat, mPerDegLng } = localMetersPerDegree(origin);
  return {
    x: (point.lng - origin.lng) * mPerDegLng,
    y: (point.lat - origin.lat) * mPerDegLat,
  };
}

function fromXY(xy, origin) {
  const { mPerDegLat, mPerDegLng } = localMetersPerDegree(origin);
  return {
    lat: origin.lat + xy.y / mPerDegLat,
    lng: origin.lng + xy.x / mPerDegLng,
  };
}

export function projectOntoBearing(origin, axisBearingDeg, hover) {
  if (!origin || !hover) return origin;
  const heading = bearingDegrees(origin, hover);
  const dist = haversineMeters(origin, hover);
  const signed = dist * Math.cos(toRad(heading - axisBearingDeg));
  return offsetPoint(origin, axisBearingDeg, signed);
}

function projectPointOnSegment(point, start, end) {
  const origin = start;
  const p = toXY(point, origin);
  const a = toXY(start, origin);
  const b = toXY(end, origin);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = 0;
  if (len2 > 0) {
    t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
  }
  const closest = { x: a.x + t * dx, y: a.y + t * dy };
  const dxp = p.x - closest.x;
  const dyp = p.y - closest.y;
  return {
    point: fromXY(closest, origin),
    t,
    distanceM: Math.sqrt(dxp * dxp + dyp * dyp),
  };
}

function segmentIntersection(a1, a2, b1, b2) {
  const origin = a1;
  const p = toXY(a1, origin);
  const p2 = toXY(a2, origin);
  const q = toXY(b1, origin);
  const q2 = toXY(b2, origin);
  const r = { x: p2.x - p.x, y: p2.y - p.y };
  const s = { x: q2.x - q.x, y: q2.y - q.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 1e-9) return null;
  const qp = { x: q.x - p.x, y: q.y - p.y };
  const t = (qp.x * s.y - qp.y * s.x) / denom;
  const u = (qp.x * r.y - qp.y * r.x) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return {
    t,
    point: fromXY({ x: p.x + t * r.x, y: p.y + t * r.y }, origin),
  };
}

function resolveSegmentHeading(start, end) {
  const course = Number(start && start.course);
  if (Number.isFinite(course) && course !== 0) {
    return normalizeBearing(course);
  }
  const endCourse = Number(end && end.course);
  if (Number.isFinite(endCourse) && endCourse !== 0) {
    return normalizeBearing(endCourse);
  }
  return bearingDegrees(start, end);
}

export function flattenTrackSegments(lines) {
  const segments = [];
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const path = (line && (line.path || line.coords)) || [];
    for (let index = 1; index < path.length; index += 1) {
      const start = path[index - 1];
      const end = path[index];
      if (
        !start ||
        !end ||
        start.lat == null ||
        start.lng == null ||
        end.lat == null ||
        end.lng == null
      ) {
        continue;
      }
      segments.push({
        start: { lat: Number(start.lat), lng: Number(start.lng), course: start.course },
        end: { lat: Number(end.lat), lng: Number(end.lng), course: end.course },
        heading: resolveSegmentHeading(start, end),
      });
    }
  });
  return segments;
}

export function snapToNearestTrack(point, lines, maxDistanceM = TRACK_SNAP_MAX_M) {
  const segments = flattenTrackSegments(lines);
  let best = null;
  segments.forEach((segment) => {
    const projected = projectPointOnSegment(point, segment.start, segment.end);
    if (!best || projected.distanceM < best.distanceM) {
      best = {
        point: projected.point,
        distanceM: projected.distanceM,
        heading: segment.heading,
        tapeBearing: perpendicularBearing(segment.heading),
      };
    }
  });
  if (!best || best.distanceM > maxDistanceM) return null;
  return best;
}

function clusterCrossings(hits, clusterM = CROSSING_CLUSTER_M) {
  if (!hits.length) return [];
  const sorted = hits.slice().sort((a, b) => a.alongM - b.alongM);
  const groups = [];
  sorted.forEach((hit) => {
    const last = groups[groups.length - 1];
    if (last && Math.abs(hit.alongM - last.alongM) <= clusterM) {
      last.hits.push(hit);
      last.alongM =
        last.hits.reduce((sum, item) => sum + item.alongM, 0) / last.hits.length;
      last.lat =
        last.hits.reduce((sum, item) => sum + item.point.lat, 0) / last.hits.length;
      last.lng =
        last.hits.reduce((sum, item) => sum + item.point.lng, 0) / last.hits.length;
      return;
    }
    groups.push({
      hits: [hit],
      alongM: hit.alongM,
      lat: hit.point.lat,
      lng: hit.point.lng,
    });
  });
  return groups.map((group, index) => ({
    index: index + 1,
    lat: group.lat,
    lng: group.lng,
    alongM: group.alongM,
  }));
}

export function measureTapeAcrossTracks(start, end, lines, options = {}) {
  const clusterM = options.clusterM != null ? options.clusterM : CROSSING_CLUSTER_M;
  const tapeLengthM = haversineMeters(start, end);
  const segments = flattenTrackSegments(lines);
  const hits = [];
  segments.forEach((segment) => {
    const intersection = segmentIntersection(start, end, segment.start, segment.end);
    if (!intersection) return;
    hits.push({
      point: intersection.point,
      alongM: tapeLengthM * intersection.t,
    });
  });
  const crossings = clusterCrossings(hits, clusterM);
  const gaps = [];
  for (let index = 1; index < crossings.length; index += 1) {
    const meters = crossings[index].alongM - crossings[index - 1].alongM;
    if (meters > clusterM) {
      gaps.push(Number(meters.toFixed(2)));
    }
  }
  const gapSumM = gaps.reduce((sum, value) => sum + value, 0);
  const gapAverageM = gaps.length ? gapSumM / gaps.length : 0;
  return {
    tapeLengthM,
    crossingCount: crossings.length,
    crossings,
    gaps,
    gapSumM,
    gapAverageM,
    snapped: Boolean(options.snapped),
    tapeBearing: options.tapeBearing != null ? options.tapeBearing : null,
  };
}

export function summarizeAreaMeasurement(points, maps) {
  return {
    areaHa: computePointsAreaHa(points, maps),
    perimeterM: computePointsPerimeterM(points, maps),
    vertexCount: points ? points.length : 0,
  };
}

export function summarizeFreeDistance(points, maps) {
  return {
    tapeLengthM: computePointsLengthMeters(points, maps),
    lastSegmentM:
      points && points.length >= 2
        ? computePointsLengthMeters(points.slice(-2), maps)
        : 0,
  };
}

export function linesFromMapProp(map) {
  if (!map) return [];
  if (Array.isArray(map.lines)) return map.lines;
  return [];
}

export function linesFromCoordinateBuckets(cordenates) {
  return (Array.isArray(cordenates) ? cordenates : []).map((item) => ({
    path: item.coords || item.path || [],
  }));
}
